import { render } from 'solid-js/web'
import _config from './config'
import env from './client/env'
import { configureStoreFactory } from '@te/volt-store'
import voltApi from './client/client-api/clientapi'
import icons from './client/client-assets'
import { AppBootstrap, LocalStorageKeys, ToastPosition, getIPAddress } from '@volt-libs/foundation'
import '@volt-libs/components/styles'
import '@volt-libs/foundation/styles'
import './index.css'
import { Navigate, RouteDefinition } from '@solidjs/router'
import { formatUrl, setLogoButton } from './utils/setLogoButtonColor'
import axios from 'axios'
import { Show, createEffect, createSignal, lazy } from 'solid-js'
import { getLanguage } from './utils/getLanguage'
import appPackage from '../package.json'

const root = document.getElementById('root')

const {
    UNKNOWN_ERROR,
    UNKNOWN_API_ERROR,
    LOGIN_REQUIRED,
    PLAYER_ENTITLEMENT_REVOKED,
    INVALID_PIN,
    LOGIN_FAILED,
    RECORDING_NO_QUOTA_SUBSCRIPTION,
    USER_SESSION_NOT_FOUND_OR_TIMEOUT,
    AUTH_OAUTH_EXPIRED_TOKEN,
    EXCEEDED_QUOTA,
    INVALID_REQUEST_PAYLOAD,
    INVALID_AUTH_TOKEN,
    OPTA_FEED_NO_DATA,
} = voltApi.VoltError.codes
const getIgnoredNotification = (config: { [key: string]: any }) => {
    return [
        // Filter out LOGIN_REQUIRED -> shall be handled by to restart starter Kit
        LOGIN_REQUIRED.code,
        // Filter out PLAYER_ENTITLEMENT_REVOKED because this error triggers a purchasing channel flow (channel not entitled)
        PLAYER_ENTITLEMENT_REVOKED.code,
        // Filter out generic errors (not précise enough to display coherent information message)
        config.env !== 'development' && UNKNOWN_ERROR.code,
        config.env !== 'development' && UNKNOWN_API_ERROR.code,
        INVALID_PIN.code,
        USER_SESSION_NOT_FOUND_OR_TIMEOUT.code,
        AUTH_OAUTH_EXPIRED_TOKEN.code,
        LOGIN_FAILED.code,
        EXCEEDED_QUOTA.code,
        INVALID_REQUEST_PAYLOAD.code,
        INVALID_AUTH_TOKEN.code,
        OPTA_FEED_NO_DATA.code,
    ]
}

const APP_ROUTES: RouteDefinition[] = [
    {
        path: '/',
        children: [
            {
                path: '/',
                component: () => <Navigate href={'/home'} />,
            },
            {
                path: '/oauthredirect',
                component: lazy(() => import('@volt-libs/foundation').then(module => ({ default: module.AuthRedirect }))),
            },
            {
                path: '/:parentNode',
                component: lazy(() => import('./App')),
                children: [
                    {
                        path: ['/', '/:childNode'],
                        component: lazy(() => import('./components/layout/Layout')),
                        children: [
                            {
                                path: '/',
                                component: lazy(() => import('./components/mainContent/MainContent')),
                            },
                        ],
                    },
                    {
                        path: ['/collection'],
                        component: lazy(() => import('./components/layout/Layout')),
                        children: [
                            {
                                path: '/:childNode',
                                component: lazy(() => import('./components/collection/Collection')),
                            },
                        ],
                    },
                ],
            },
            {
                path: '/user-search',
                component: lazy(() => import('./App')),
                children: [
                    {
                        path: '/',
                        component: lazy(() => import('./components/layout/Layout')),
                        children: [
                            {
                                path: '/',
                                component: lazy(() => import('./pages/searchPage/SearchPage')),
                            },
                        ],
                    },
                ],
            },
            {
                path: '/user-settings',
                component: lazy(() => import('./App')),
                children: [
                    {
                        path: '/',
                        component: lazy(() => import('./components/layout/Layout')),
                        children: [
                            {
                                path: '/',
                                component: lazy(() => import('./pages/userSettings/UserSettings')),
                            },
                        ],
                    },
                ],
            },
            {
                path: '/transaction-list',
                component: lazy(() => import('./App')),
                children: [
                    {
                        path: '/',
                        component: lazy(() => import('./components/layout/Layout')),
                        children: [
                            {
                                path: '/',
                                component: lazy(() => import('./pages/transactionList/TransactionList')),
                            },
                        ],
                    },
                ],
            },
            {
                path: '/player/:id',
                component: lazy(() => import('./App')),
                children: [
                    {
                        path: '/',
                        component: lazy(() => import('./components/player/Player')),
                    },
                ],
            },
            {
                path: '/view-more/:requestId',
                component: lazy(() => import('./App')),
                children: [
                    {
                        path: '/',
                        component: lazy(() => import('./components/layout/Layout')),
                        children: [
                            {
                                path: '/',
                                component: lazy(() => import('./components/viewMore/ViewMore')),
                            },
                        ],
                    },
                ],
            },
            {
                path: '/view-more-recordings/:requestId',
                component: lazy(() => import('./App')),
                children: [
                    {
                        path: '/',
                        component: lazy(() => import('./components/layout/Layout')),
                        children: [
                            {
                                path: '/',
                                component: lazy(() => import('./pages/recordingPage/layouts/ViewMore')),
                            },
                        ],
                    },
                ],
            },
            {
                path: '*',
                component: () => <Navigate href={'/home'} />,
            },
        ],
    },
]

/*** Routes not for production ***/
const APP_ROUTES_DEV_ONLY = [
    {
        path: '/settings',
        component: lazy(() => import('./pages/settings/Settings')),
    },
]

render((): any => {
    const [defaultConfig, setDefaultConfig] = createSignal<{ [key: string]: any }>()
    
    createEffect(async () => {
        await getIPAddress()
        const config = await _config()

        if (!localStorage.getItem('EMI_POPULATION') && !localStorage.getItem('ENV')) {
            localStorage.setItem('EMI_POPULATION', config?.urls?.emi?.defaultPopulation)
            localStorage.setItem('ENV', config.env)
        }

        if (localStorage.getItem('APP_VERSION') !== config?.version) {
            localStorage.setItem('APP_VERSION', config?.version)
        }
        
        setDefaultConfig(config)
        const population: any = localStorage.getItem('EMI_POPULATION') || config?.urls?.emi?.defaultPopulation
        const url = formatUrl(config?.urls?.emi?.baseUrl, config?.urls?.emi?.homePath, population)
        axios
            .get(`${url}/revision.json`)
            .then(res => axios.get(`${url}/${res.data.revision}.json`))
            .then(r => setLogoButton(r.data, config))
            .catch(e => console.error(e))
    })

    return (
        <Show when={defaultConfig()}>
            <AppBootstrap
                ignoredNotification={getIgnoredNotification(defaultConfig)}
                defaultConfig={defaultConfig()}
                storeOptions={{
                    voltApi,
                    configureStoreFactory,
                    env
                }}
                toastPosition={ToastPosition.TOP_CENTER}
                icons={icons}
                routes={[...APP_ROUTES, ...APP_ROUTES_DEV_ONLY]}
                getLanguage={getLanguage}
            />
        </Show>
    )
}, root!)
